import { isGroupAdmin } from '@/features/dashboard/power/group/groupDisplay'
import { isFeatureFlagEnabled } from '@/features/dashboard/site-admin/orgFeatureFlags'
import { locationScopeAllowsEntry } from '@/lib/locationScope'
import { SCHOOL_FLAVOUR } from '@/lib/terminology'
import { hasRole } from '@/utils/hasRole'

export function canAccessNavEntry(
    entry,
    { isAuthenticated, isStaff, user, activeOrg }
) {
    if (entry.authRequired && !isAuthenticated) {
        return false
    }

    // A location member (site manager / tenant) reaches only entries marked
    // `locationScopeAware`, mirroring the backend's fail-closed location-scope gate.
    if (!locationScopeAllowsEntry(entry, user)) {
        return false
    }

    if (entry.staffRequired && !isStaff) {
        return false
    }

    // Billing-gated links (e.g. Holidays, Insights, Alerts) are hidden when the
    // org's billing is explicitly inactive. Mirror the `=== false` guard used in
    // PowerExplore so a missing/unknown flag leaves the link visible.
    if (entry.requiresBilling && activeOrg?.is_billing_active === false) {
        return false
    }

    // Feature-flag-gated links are opt-in: hidden unless the org has the flag
    // explicitly enabled (the inverse of the fail-open billing guard above).
    if (
        entry.requiresFeatureFlag &&
        !isFeatureFlagEnabled(activeOrg, entry.requiresFeatureFlag)
    ) {
        return false
    }

    // Flavour-gated links are opt-in: hidden unless the active org's flavour
    // is one of the entry's allowed flavours.
    if (
        entry.requiresFlavour?.length &&
        !entry.requiresFlavour.includes(activeOrg?.flavour || SCHOOL_FLAVOUR)
    ) {
        return false
    }

    // Group-admin-gated links (the MAT/LA group dashboard) are visible only to
    // users the API reports as administering at least one org group. This is
    // API-derived, not role-derived: designation lives in the group's admins
    // M2M, surfaced on the me payload as `administeredGroups`.
    if (entry.requiresGroupAdmin && !isGroupAdmin(user)) {
        return false
    }

    if (entry.roles?.length) {
        if (!user) {
            return false
        }

        return Boolean(hasRole(user, entry.roles))
    }

    return true
}

export function getVisibleNavItems(items = [], access) {
    return items.filter((item) => canAccessNavEntry(item, access))
}

export function canAccessNavSection(section, access) {
    if (!canAccessNavEntry(section, access)) {
        return false
    }

    return getVisibleNavItems(section.items, access).length > 0
}
